import { useEffect, useRef, useState } from "react";
import { Check, Copy, FileDown, Loader2, Share2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/library/dialog";
import { WhatsAppIcon } from "@/utils/Icons";
import { DEFAULT_COUNTRY_CODE, toDialableDigits } from "@/constants/countryCodes";
import { downloadPreparedPdf, type PreparedPdf } from "./buildReportPdf";
import { apiErrorText, uploadReportPdf, type ReportShareLink } from "./shareApi";
import type { PreparePdf } from "./ShareMenu";

/**
 * Send a report PDF on WhatsApp.
 *
 * A web page cannot attach a file to a WhatsApp chat, so there are two roads:
 *   - "Share PDF file" — the device's share sheet with the PDF itself, where
 *     the browser supports sharing files (phones, Safari). Pick WhatsApp there.
 *   - "Open WhatsApp" — wa.me with the message and a signed 7-day link to the
 *     PDF (desktop Chrome/Edge have no file share sheet).
 * Both work for every workspace: no WhatsApp Business number, template or
 * 24-hour window is involved — it goes from the user's own WhatsApp.
 */

const LAST_NUMBER_KEY = "xale.reportShare.lastWhatsappNumber";

const readLastNumber = (): string => {
  try {
    return localStorage.getItem(LAST_NUMBER_KEY) ?? "";
  } catch {
    return "";
  }
};
const saveLastNumber = (value: string) => {
  try {
    if (value) localStorage.setItem(LAST_NUMBER_KEY, value);
    else localStorage.removeItem(LAST_NUMBER_KEY);
  } catch {
    /* private mode — not remembering is fine */
  }
};

const fileOf = (pdf: PreparedPdf) => new File([pdf.blob], pdf.fileName, { type: "application/pdf" });

const canShareFile = (pdf: PreparedPdf | null): boolean => {
  if (!pdf || typeof navigator === "undefined" || !navigator.canShare) return false;
  try {
    return navigator.canShare({ files: [fileOf(pdf)] });
  } catch {
    return false;
  }
};

const sizeLabel = (bytes: number) =>
  bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

function composeMessage(pdf: PreparedPdf, link: ReportShareLink | null): string {
  const lines = [`*${pdf.title}*`, ...pdf.summary];
  if (link) lines.push("", link.url, "(PDF link — opens for 7 days)");
  return lines.join("\n");
}

type Step = "pdf" | "link" | "ready";

export default function ShareWhatsappDialog({
  title,
  prepare,
  onClose,
}: {
  title: string;
  prepare: PreparePdf;
  onClose: () => void;
}) {
  const [step, setStep] = useState<Step>("pdf");
  const [progress, setProgress] = useState<string | null>(null);
  const [pdf, setPdf] = useState<PreparedPdf | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [link, setLink] = useState<ReportShareLink | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [number, setNumber] = useState(readLastNumber);
  const [message, setMessage] = useState("");
  const [copied, setCopied] = useState(false);
  const edited = useRef(false);

  // Render, then upload — once per opening. The dialog unmounts on close,
  // so a late result from a closed dialog has nowhere to land.
  useEffect(() => {
    let alive = true;
    (async () => {
      let made: PreparedPdf;
      try {
        made = await prepare((done, total) => {
          if (alive && total > 1) setProgress(`${done} of ${total}`);
        });
      } catch (error) {
        if (alive) setPdfError((error as Error)?.message || "Couldn't create the PDF.");
        return;
      }
      if (!alive) return;
      setPdf(made);
      setMessage(composeMessage(made, null));
      setStep("link");
      try {
        const minted = await uploadReportPdf(made);
        if (!alive) return;
        setLink(minted);
        if (!edited.current) setMessage(composeMessage(made, minted));
      } catch (error) {
        if (alive) setLinkError(apiErrorText(error, "Couldn't create a link to the PDF."));
      } finally {
        if (alive) setStep("ready");
      }
    })();
    return () => {
      alive = false;
    };
    // prepare is called once for this opening on purpose.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const digits = number.trim() ? toDialableDigits(number, DEFAULT_COUNTRY_CODE) : "";
  const numberInvalid = number.trim() !== "" && digits === "";

  const openWhatsapp = () => {
    if (numberInvalid) return;
    saveLastNumber(number.trim());
    const url = `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const shareFile = async () => {
    if (!pdf) return;
    try {
      await navigator.share({ files: [fileOf(pdf)], title: pdf.title, text: pdf.title });
    } catch (error) {
      if ((error as DOMException)?.name !== "AbortError") toast.error("Your browser couldn't share the file.");
    }
  };

  const copyLink = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Couldn't copy — select the link in the message instead.");
    }
  };

  const fileShare = canShareFile(pdf);
  const busy = step !== "ready" && !pdfError;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="gap-0 rounded-[20px] p-0 sm:max-w-[460px]">
        <DialogHeader className="border-b border-[#eef1f0] px-5 pb-3 pt-5 text-left">
          <DialogTitle className="flex items-center gap-2 text-[16px] font-medium text-[#051912]">
            <WhatsAppIcon className="size-4 text-[#25D366]" />
            Send on WhatsApp
          </DialogTitle>
          <DialogDescription className="truncate text-[12px] text-[#697571]">{title}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3 px-5 py-4">
          <div className="flex items-center gap-2 rounded-[12px] bg-[#f4faf7] px-3 py-2 text-[12px] text-[#1e4635]">
            {pdfError ? (
              <span className="text-[#9b1c31]">{pdfError}</span>
            ) : step === "pdf" ? (
              <>
                <Loader2 className="size-3.5 animate-spin text-[#218760]" />
                Preparing PDF{progress ? ` · ${progress}` : "…"}
              </>
            ) : (
              <>
                <Check className="size-3.5 text-[#218760]" />
                PDF ready · {pdf!.pageCount} page{pdf!.pageCount === 1 ? "" : "s"} · {sizeLabel(pdf!.blob.size)}
                {step === "link" && (
                  <span className="ml-auto flex items-center gap-1 text-[#697571]">
                    <Loader2 className="size-3 animate-spin" /> creating link
                  </span>
                )}
              </>
            )}
          </div>

          {linkError && (
            <p role="alert" className="rounded-[10px] border border-[#f3c6ce] bg-[#fdf1f3] px-3 py-2 text-[12px] text-[#9b1c31]">
              {linkError} {fileShare ? "Use “Share PDF file” instead," : "You can"} download the PDF and attach it in WhatsApp.
            </p>
          )}

          <label className="flex flex-col gap-1 text-[12px] text-[#3d4a45]">
            Send to (optional)
            <input
              value={number}
              onChange={(e) => setNumber(e.target.value)}
              inputMode="tel"
              placeholder="+91 98765 43210"
              aria-invalid={numberInvalid}
              className="rounded-[10px] border border-[#d6ebe3] bg-white px-3 py-2 text-[13px] text-[#051912] outline-none placeholder:text-[#9ba3a0] focus:border-[#218760] aria-[invalid=true]:border-[#da2a46]"
            />
            <span className={`text-[11px] ${numberInvalid ? "text-[#da2a46]" : "text-[#9ba3a0]"}`}>
              {numberInvalid
                ? "Enter the number with its country code, e.g. +91 98765 43210."
                : "Leave empty to pick the chat inside WhatsApp."}
            </span>
          </label>

          <label className="flex flex-col gap-1 text-[12px] text-[#3d4a45]">
            Message
            <textarea
              value={message}
              onChange={(e) => {
                edited.current = true;
                setMessage(e.target.value);
              }}
              rows={5}
              disabled={!pdf}
              className="resize-none rounded-[10px] border border-[#d6ebe3] bg-white px-3 py-2 text-[12px] leading-relaxed text-[#051912] outline-none focus:border-[#218760] disabled:bg-[#f7f9f8]"
            />
          </label>
          {link && (
            <p className="text-[11px] text-[#9ba3a0]">Anyone with the link can open this PDF for 7 days.</p>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-[#eef1f0] px-5 py-3">
          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={!pdf}
              onClick={() => pdf && downloadPreparedPdf(pdf)}
              className="flex items-center gap-1.5 rounded-full px-2.5 py-1.5 text-[12px] font-medium text-[#1e4635] transition-colors hover:bg-[#eaf5f1] disabled:opacity-40"
            >
              <FileDown className="size-3.5" /> Download
            </button>
            {link && (
              <button
                type="button"
                onClick={() => void copyLink()}
                className="flex items-center gap-1.5 rounded-full px-2.5 py-1.5 text-[12px] font-medium text-[#1e4635] transition-colors hover:bg-[#eaf5f1]"
              >
                {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />} {copied ? "Copied" : "Copy link"}
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {fileShare && (
              <button
                type="button"
                onClick={() => void shareFile()}
                title="Attach the PDF itself — choose WhatsApp in the share sheet"
                className="flex items-center gap-1.5 rounded-full border border-[#d6ebe3] bg-white px-3 py-1.5 text-[12px] font-medium text-[#1e4635] transition-colors hover:bg-[#eaf5f1]"
              >
                <Share2 className="size-3.5" /> Share PDF file
              </button>
            )}
            <button
              type="button"
              disabled={busy || !link || numberInvalid}
              onClick={openWhatsapp}
              className="flex items-center gap-1.5 rounded-full bg-[#218760] px-3.5 py-1.5 text-[12px] font-medium text-white transition-colors hover:bg-[#1b6f4f] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy ? <Loader2 className="size-3.5 animate-spin" /> : <WhatsAppIcon className="size-3.5" />}
              Open WhatsApp
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
